import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  X,
  AlertCircle
} from 'lucide-react';
import { apiRequest } from '../utils/api';

const PayoutRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('PENDING');

  useEffect(() => {
    fetchPayouts();
  }, [filterStatus]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/tickets`;
      if (filterStatus !== 'ALL') {
        url = `/api/admin/tickets/status/${filterStatus}`;
      }
      
      const response = await apiRequest(url);
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des demandes de versement');
      }
      
      const data = await response.json();
      
      // Filter only PAYOUT_REQUEST tickets
      const payoutTickets = data.filter(ticket => ticket.type === 'PAYOUT_REQUEST');
      
      setRequests(payoutTickets);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Impossible de charger les demandes de versement.');
    } finally {
      setLoading(false);
    }
  };

  const parseMetadata = (metadataStr) => {
    try {
      return metadataStr ? JSON.parse(metadataStr) : {};
    } catch (e) {
      return {};
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Voulez-vous vraiment approuver ce versement ?')) return;
    
    try {
      const response = await apiRequest(`/api/admin/tickets/${id}/approve-payout`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: 'Approuvé par le support' })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'approbation');
      }
      
      alert('Versement approuvé avec succès');
      fetchPayouts();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Veuillez indiquer le motif du rejet :');
    if (reason === null) return; // User cancelled
    if (reason.trim() === '') {
      alert('Le motif de rejet est obligatoire');
      return;
    }
    
    try {
      const response = await apiRequest(`/api/admin/tickets/${id}/reject-payout`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: reason })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du rejet');
      }
      
      alert('Versement rejeté avec succès');
      fetchPayouts();
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div style={styles.container} className="pr-container">
      <style>{`
        .pr-table-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
        }
        @media (max-width: 768px) {
          .pr-container {
            padding: 12px 8px !important;
          }
          .pr-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
            margin-bottom: 1.25rem !important;
          }
          .pr-filters {
            width: 100% !important;
          }
          .pr-filters select {
            width: 100% !important;
          }
          .pr-title {
            font-size: 1.4rem !important;
          }
        }
      `}</style>
      <div style={styles.header} className="pr-header">
        <div>
          <h1 style={styles.title} className="pr-title">Demandes de Versements</h1>
          <p style={styles.subtitle}>Gérez les retraits et versements d'argent des chauffeurs</p>
        </div>
        
        <div style={styles.filters} className="pr-filters">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.select}
          >
            <option value="ALL">Tous les statuts</option>
            <option value="PENDING">En attente (PENDING)</option>
            <option value="APPROVED">Approuvé (APPROVED)</option>
            <option value="REJECTED">Rejeté (REJECTED)</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <p>Chargement des demandes...</p>
      ) : requests.length === 0 ? (
        <div style={styles.emptyState}>
          <p>Aucune demande de versement trouvée.</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <div className="pr-table-scroll">
            <table style={{ ...styles.table, minWidth: '680px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Chauffeur</th>
                  <th style={styles.th}>Montant</th>
                  <th style={styles.th}>IBAN</th>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => {
                  const metadata = parseMetadata(req.metadata);
                  return (
                    <tr key={req.id} style={styles.tr}>
                      <td style={styles.td}>
                        {new Date(req.createdAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        {req.driverName}<br/>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 'bold', color: '#1e293b' }}>
                          {metadata.amount ? metadata.amount.toFixed(2) + ' €' : '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontFamily: 'monospace', backgroundColor: '#f1f5f9', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                          {metadata.iban || '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.badge(req.status === 'APPROVED' ? '#10b981' : req.status === 'REJECTED' ? '#ef4444' : '#f59e0b')}>
                          {req.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {req.status === 'PENDING' && (
                          <div style={styles.actions}>
                            <button 
                              onClick={() => handleApprove(req.id)}
                              style={{ ...styles.btn, backgroundColor: '#10b981', color: 'white' }}
                              title="Approuver"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button 
                              onClick={() => handleReject(req.id)}
                              style={{ ...styles.btn, backgroundColor: '#ef4444', color: 'white' }}
                              title="Rejeter"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    marginTop: '0.5rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    outline: 'none',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: '600',
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    verticalAlign: 'middle',
  },
  tr: {
    '&:hover': {
      backgroundColor: '#f8fafc',
    }
  },
  badge: (color) => ({
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    backgroundColor: `${color}20`,
    color: color,
    fontSize: '0.875rem',
    fontWeight: '500',
  }),
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  btn: {
    padding: '0.5rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s',
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem',
    backgroundColor: 'white',
    borderRadius: '1rem',
    color: '#64748b',
  }
};

export default PayoutRequests;
